import { createApp, type App, type Component } from 'vue';
import { createPinia } from 'pinia';
import { primeVueOptions } from './config/primeVueTheme';
import PrimeVue from 'primevue/config';
import VueKeyCloak from '@dsb-norge/vue-keycloak-js';
import keycloakConfig from '@/config/keycloak';
import sentryConfig from '@/config/sentry';
import * as Sentry from '@sentry/browser';
import routerFactory from './router';
import { tokenHasRequiredResourceAccess } from './plugins/permissions';
import Keycloak from 'keycloak-js';
import { useRedirect } from './composables/useRedirect';
import i18n from '@/i18n';
import ToastService from 'primevue/toastservice';

function appFactory(rootComponent: Component, extendApp: (app: App) => Promise<void> | void) {
  if (sentryConfig.dsn) {
    Sentry.init(sentryConfig);
  }

  try {
    const app = createApp(rootComponent);

    app.use(createPinia());
    app.use(PrimeVue, primeVueOptions);
    app.use(i18n);
    app.use(ToastService);

    app.use(VueKeyCloak, {
      init: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
        checkLoginIframe: false,
        messageReceiveTimeout: 5000
      },
      config: keycloakConfig,
      onReady: async (keycloak) => {
        await onKeycloakReady(keycloak, app, extendApp);
      },
      onInitError(error) {
        console.error('Failed to init SSO!', error);
        useRedirect().toError();
      }
    });
  } catch (error) {
    console.error('Failed to bootstrap app!', error);
    useRedirect().toError();
  }
}

async function onKeycloakReady(
  keycloak: Keycloak,
  app: App,
  extendApp: (app: App) => Promise<void> | void
) {
  if (!keycloak.authenticated) {
    keycloak.createLoginUrl().then((url) => useRedirect().toLogin(url));
    return;
  }

  setSentryUserIfEnabled(keycloak);

  if (!tokenHasRequiredResourceAccess(keycloak.tokenParsed!)) {
    console.warn('User without permissions attempted login.');
    useRedirect().to403();
    return;
  }

  // NOTE: must be a function, otherwise Keycloak route params are
  // note cleared from the URL properly
  app.use(routerFactory());

  try {
    await extendApp(app);
  } catch (error) {
    console.error(error);
    useRedirect().toError();
    return;
  }

  app.mount('#app');
}

function setSentryUserIfEnabled(keycloak: Keycloak) {
  if (sentryConfig.dsn) {
    Sentry.setUser({
      email: keycloak.tokenParsed!.email,
      id: keycloak.tokenParsed!.sub
    });
  }
}

export default appFactory;
